import { ProductHowItWorksContent, ProductHowItWorksTab } from '../shared/components/product-subpage/product-how-it-works/product-how-it-works.interface';
import {
  ProductProgramBenefitCard,
  ProductProgramBenefitStat,
  ProductProgramBenefitsContent,
} from '../shared/components/product-subpage/product-program-benefits/product-program-benefits.interface';
import {
  ProductTestimonial,
  ProductTestimonialsContent,
} from '../shared/components/product-subpage/product-testimonials/product-testimonials.interface';

export { ProductHowItWorksContent } from '../shared/components/product-subpage/product-how-it-works/product-how-it-works.interface';
export { ProductProgramBenefitsContent } from '../shared/components/product-subpage/product-program-benefits/product-program-benefits.interface';
export { ProductTestimonialsContent } from '../shared/components/product-subpage/product-testimonials/product-testimonials.interface';

export const PRODUCT_INTRO_BANNER_ASSETS = {
  mediaImageSrc: '/assets/images/myci-student-dashboard-video-thumbnail.svg',
  videoSrc: 'resnnbgtho',
};

interface ProductHowItWorksTabAssets {
  id: string;
  labelKey: string;
  cards: { imageSrc: string; imageAltKey: string; titleKey: string; descriptionKey: string }[];
}

const PRODUCT_HOW_IT_WORKS_I18N_KEYS = {
  headline: 'invest.howItWorks.headline',
};

const PRODUCT_HOW_IT_WORKS_TABS_ASSETS: ProductHowItWorksTabAssets[] = [
  {
    id: 'teachers',
    labelKey: 'invest.howItWorks.tabs.teachers.label',
    cards: [
      {
        imageSrc: '/assets/images/myce-investor-teachers-customize-experience-1.svg',
        imageAltKey: 'invest.howItWorks.tabs.teachers.cards.card_0.imageAlt',
        titleKey: 'invest.howItWorks.tabs.teachers.cards.card_0.title',
        descriptionKey: 'invest.howItWorks.tabs.teachers.cards.card_0.description',
      },
      {
        imageSrc: '/assets/images/myce-investor-teachers-customize-experience.svg',
        imageAltKey: 'invest.howItWorks.tabs.teachers.cards.card_1.imageAlt',
        titleKey: 'invest.howItWorks.tabs.teachers.cards.card_1.title',
        descriptionKey: 'invest.howItWorks.tabs.teachers.cards.card_1.description',
      },
      {
        imageSrc: '/assets/images/myce-investor-teachers-learning-library.svg',
        imageAltKey: 'invest.howItWorks.tabs.teachers.cards.card_2.imageAlt',
        titleKey: 'invest.howItWorks.tabs.teachers.cards.card_2.title',
        descriptionKey: 'invest.howItWorks.tabs.teachers.cards.card_2.description',
      },
      {
        imageSrc: '/assets/images/myce-investor-teachers-monitor-portfolios.svg',
        imageAltKey: 'invest.howItWorks.tabs.teachers.cards.card_3.imageAlt',
        titleKey: 'invest.howItWorks.tabs.teachers.cards.card_3.title',
        descriptionKey: 'invest.howItWorks.tabs.teachers.cards.card_3.description',
      },
    ],
  },
  {
    id: 'students',
    labelKey: 'invest.howItWorks.tabs.students.label',
    cards: [
      {
        imageSrc: '/assets/images/myce-investor-students-explore-investing.svg',
        imageAltKey: 'invest.howItWorks.tabs.students.cards.card_0.imageAlt',
        titleKey: 'invest.howItWorks.tabs.students.cards.card_0.title',
        descriptionKey: 'invest.howItWorks.tabs.students.cards.card_0.description',
      },
      {
        imageSrc: '/assets/images/myce-investor-students-smart-habits.svg',
        imageAltKey: 'invest.howItWorks.tabs.students.cards.card_1.imageAlt',
        titleKey: 'invest.howItWorks.tabs.students.cards.card_1.title',
        descriptionKey: 'invest.howItWorks.tabs.students.cards.card_1.description',
      },
      {
        imageSrc: '/assets/images/myce-investor-students-market-events.svg',
        imageAltKey: 'invest.howItWorks.tabs.students.cards.card_2.imageAlt',
        titleKey: 'invest.howItWorks.tabs.students.cards.card_2.title',
        descriptionKey: 'invest.howItWorks.tabs.students.cards.card_2.description',
      },
      {
        imageSrc: '/assets/images/myce-investor-students-score-bug.svg',
        imageAltKey: 'invest.howItWorks.tabs.students.cards.card_3.imageAlt',
        titleKey: 'invest.howItWorks.tabs.students.cards.card_3.title',
        descriptionKey: 'invest.howItWorks.tabs.students.cards.card_3.description',
      },
    ],
  },
];

export function buildHowItWorksContent(t: (key: string) => string): ProductHowItWorksContent {
  const tabs: ProductHowItWorksTab[] = PRODUCT_HOW_IT_WORKS_TABS_ASSETS.map((tabAsset) => ({
    id: tabAsset.id,
    label: t(tabAsset.labelKey),
    cards: tabAsset.cards.map((card) => ({
      imageSrc: card.imageSrc,
      imageAlt: t(card.imageAltKey),
      title: t(card.titleKey),
      description: t(card.descriptionKey),
    })),
  }));

  return {
    headline: t(PRODUCT_HOW_IT_WORKS_I18N_KEYS.headline),
    tabs,
  };
}

interface ProgramBenefitStatAssets {
  valueKey: string;
  labelKey: string;
}

interface ProgramBenefitCardAssets {
  iconSrc: string;
  titleKey: string;
  descriptionKey: string;
}

interface TestimonialAssets {
  quoteKey: string;
  authorNameKey: string;
  authorRoleKey: string;
}

const PROGRAM_BENEFITS_I18N_KEYS = {
  headline: 'invest.programBenefits.headline',
  description: 'invest.programBenefits.description',
};

const PROGRAM_BENEFITS_STATS_ASSETS: ProgramBenefitStatAssets[] = [
  { valueKey: 'invest.programBenefits.stats.stat_0.value', labelKey: 'invest.programBenefits.stats.stat_0.label' },
  { valueKey: 'invest.programBenefits.stats.stat_1.value', labelKey: 'invest.programBenefits.stats.stat_1.label' },
  { valueKey: 'invest.programBenefits.stats.stat_2.value', labelKey: 'invest.programBenefits.stats.stat_2.label' },
];

const PROGRAM_BENEFITS_CARDS_ASSETS: ProgramBenefitCardAssets[] = [
  {
    iconSrc: '/assets/images/myce-program-benefits-habits.svg',
    titleKey: 'invest.programBenefits.cards.card_0.title',
    descriptionKey: 'invest.programBenefits.cards.card_0.description',
  },
  {
    iconSrc: '/assets/images/myce-program-benefits-home.svg',
    titleKey: 'invest.programBenefits.cards.card_1.title',
    descriptionKey: 'invest.programBenefits.cards.card_1.description',
  },
  {
    iconSrc: '/assets/images/myce-program-benefits-home-alt.svg',
    titleKey: 'invest.programBenefits.cards.card_2.title',
    descriptionKey: 'invest.programBenefits.cards.card_2.description',
  },
  {
    iconSrc: '/assets/images/myce-program-benefits-home-alt-2.svg',
    titleKey: 'invest.programBenefits.cards.card_3.title',
    descriptionKey: 'invest.programBenefits.cards.card_3.description',
  },
];

const TESTIMONIALS_ASSETS: TestimonialAssets[] = [
  {
    quoteKey: 'invest.testimonials.testimonial_0.quote',
    authorNameKey: 'invest.testimonials.testimonial_0.authorName',
    authorRoleKey: 'invest.testimonials.testimonial_0.authorRole',
  },
  {
    quoteKey: 'invest.testimonials.testimonial_1.quote',
    authorNameKey: 'invest.testimonials.testimonial_1.authorName',
    authorRoleKey: 'invest.testimonials.testimonial_1.authorRole',
  },
  {
    quoteKey: 'invest.testimonials.testimonial_2.quote',
    authorNameKey: 'invest.testimonials.testimonial_2.authorName',
    authorRoleKey: 'invest.testimonials.testimonial_2.authorRole',
  },
  {
    quoteKey: 'invest.testimonials.testimonial_3.quote',
    authorNameKey: 'invest.testimonials.testimonial_3.authorName',
    authorRoleKey: 'invest.testimonials.testimonial_3.authorRole',
  },
  {
    quoteKey: 'invest.testimonials.testimonial_4.quote',
    authorNameKey: 'invest.testimonials.testimonial_4.authorName',
    authorRoleKey: 'invest.testimonials.testimonial_4.authorRole',
  },
];

export function buildProgramBenefitsContent(t: (key: string) => string): ProductProgramBenefitsContent {
  const stats: ProductProgramBenefitStat[] = PROGRAM_BENEFITS_STATS_ASSETS.map((stat) => ({
    value: t(stat.valueKey),
    label: t(stat.labelKey),
  }));

  const cards: ProductProgramBenefitCard[] = PROGRAM_BENEFITS_CARDS_ASSETS.map((card) => ({
    iconSrc: card.iconSrc,
    title: t(card.titleKey),
    description: t(card.descriptionKey),
  }));

  return {
    headline: t(PROGRAM_BENEFITS_I18N_KEYS.headline),
    description: t(PROGRAM_BENEFITS_I18N_KEYS.description),
    stats,
    cards,
  };
}

export function buildTestimonialsContent(t: (key: string) => string): ProductTestimonialsContent {
  const testimonials: ProductTestimonial[] = TESTIMONIALS_ASSETS.map(
    (testimonial) => ({
      quote: t(testimonial.quoteKey),
      authorName: t(testimonial.authorNameKey),
      authorRole: t(testimonial.authorRoleKey),
    }),
  );

  return { testimonials };
}
